<template>
  <div class="courses-view">
    <Sidebar @toggle-sidebar="toggleSidebar" :class="{ 'collapsed': !isSidebarOpen }" />
    
    <div class="content-wrapper" :class="{ 'sidebar-open': isSidebarOpen }">
      <MobileHeader title="Course Management" @toggle-sidebar="toggleSidebar" />
      
      <div class="content-container">
        <header class="page-header">
          <div class="header-content">
            <div class="left-section">
              <h1 class="page-title">Courses</h1>
              <div class="header-subtitle">Manage your learning content</div>
            </div>
            <div class="right-section">
              <div class="header-actions">
                <div class="action-buttons">
                  <button class="btn-icon" title="Export Courses" @click="exportCourses">
                    <i class="icon-export"></i>
                    <span class="btn-tooltip">Export</span>
                  </button>
                  <button class="btn-icon" title="AI Assistant" @click="showAIDialog = true">
                    <i class="icon-ai"></i>
                    <span class="btn-tooltip">AI Assistant</span>
                  </button>
                  <button class="btn-primary create-btn" @click="$router.push('/course-maker')">
                    <i class="icon-plus"></i>
                    <span>Create Course</span>
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Statistics row as second line -->
            <div class="stats-row">
              <div class="stat-card">
                <div class="stat-value">{{ total }}</div>
                <div class="stat-label">Total Courses</div>
              </div>
<!--               
              <div class="stat-card">
                <div class="stat-value">{{ statistics?.activeCourses || countPublishedCourses }}</div>
                <div class="stat-label">Active Courses</div>
              </div>
              
              <div class="stat-card">
                <div class="stat-value">{{ statistics?.totalLessons || totalLessonsCount }}</div>
                <div class="stat-label">Total Lessons</div>
              </div> -->
              
              <div class="stat-card">
                <div class="stat-value">{{ instructors?.length || 0 }}</div>
                <div class="stat-label">Instructors</div>
              </div>
            </div>
          </div>
        </header>
        
        <section class="filters-section">
          <FilterControls 
            :categories="categories" 
            :instructors="instructors"
            :initial-filters="filters"
            :initial-search="searchQuery"
            @filter-change="updateFilters"
            @search-change="updateSearch"
          />
          
          <div class="controls-row">
            <SortingControls
              :sort-key="sortKey"
              :sort-order="sortOrder"
              @sort="handleSort"
            />
            
            <div class="view-toggle">
              <button 
                class="view-btn" 
                :class="{ active: viewMode === 'grid' }" 
                @click="viewMode = 'grid'"
                title="Grid View"
              >
                <i class="icon-grid"></i>
              </button>
              <button 
                class="view-btn" 
                :class="{ active: viewMode === 'list' }" 
                @click="viewMode = 'list'"
                title="List View"
              >
                <i class="icon-list"></i>
              </button>
            </div>
          </div>
        </section>
        
        <BulkActionsBar
          v-if="selectedCourses.length > 0"
          :selected-count="selectedCourses.length"
          @delete="confirmBulkDelete"
          @category="showCategoryDialog = true"
          @status="showStatusDialog = true"
          @deselect-all="deselectAllCourses"
        />
        
        <section class="courses-display">
          <div v-if="loading" class="loading-container">
            <div class="loader"></div>
            <p>Loading courses...</p>
          </div>
          
          <div v-else-if="filteredCourses.length === 0" class="empty-container">
            <div class="empty-state">
              <i class="icon-empty-courses"></i>
              <h2>No courses found</h2>
              <p>{{ hasActiveFilters ? 'Try adjusting your search filters' : 'Get started by creating your first course' }}</p>
              <div class="empty-actions">
                <button v-if="hasActiveFilters" class="btn-secondary" @click="resetAllFilters">
                  Clear Filters
                </button>
                <button v-else class="btn-primary" @click="$router.push('/course-maker')">
                  Create Course
                </button>
              </div>
            </div>
          </div>
          
          <template v-else>
            <!-- Grid View -->
            <div v-if="viewMode === 'grid'" class="courses-grid">
              <div 
                v-for="course in filteredCourses" 
                :key="course.id" 
                class="course-card"
                :class="{ 'selected': selectedCourses.includes(course.id) }"
              >
                <div class="course-card-header">
                  <div class="course-thumbnail">
                    <img v-if="course.urls.image_url" :src="course.urls.image_url" :alt="course.title">
                    <div v-else class="thumbnail-placeholder">
                      <i class="icon-course"></i>
                    </div>
                  </div>
                  <div v-if="selectionMode" class="selection-checkbox">
                    <input 
                      type="checkbox" 
                      :id="`course-${course.id}`" 
                      :checked="selectedCourses.includes(course.id)"
                      @change="toggleCourseSelection(course.id)"
                    >
                    <label :for="`course-${course.id}`"></label>
                  </div>
                </div>
                
                <div class="course-card-body">
                  <h3 class="course-title" @click="viewCourse(course.id)">{{ course.title }}</h3>
                  <p class="course-description">{{ truncateText(course.description, 100) }}</p>
                  
                  <div class="course-meta">
                    <div class="meta-item lessons">
                      <i class="icon-lesson"></i>
                      <span>{{ course.lessons_count || 0 }} lessons</span>
                    </div>
                    <div class="meta-item status">
                      <span class="status-badge" :class="course.status">{{ course.status }}</span>
                    </div>
                  </div>
                </div>
                
                <div class="course-card-footer">
                  <div class="card-actions">
                    <button class="action-btn view" @click="viewCourse(course.id)" title="View Course">
                      <i class="icon-view"></i>
                    </button>
                    <button class="action-btn edit" @click="editCourse(course.id)" title="Edit Course">
                      <i class="icon-edit"></i>
                    </button>
                    <button class="action-btn delete" @click="confirmDelete(course.id)" title="Delete Course">
                      <i class="icon-delete"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- List View -->
            <div v-else class="courses-list">
              <table class="courses-table">
                <thead>
                  <tr>
                    <th v-if="selectionMode" class="th-checkbox">
                      <input 
                        type="checkbox" 
                        id="select-all" 
                        :checked="isAllSelected"
                        @change="toggleSelectAll"
                      >
                      <label for="select-all"></label>
                    </th>
                    <th class="th-title">Title</th>
                    <th class="th-instructor">Instructor</th>
                    <th class="th-category">Category</th>
                    <th class="th-lessons">Lessons</th>
                    <th class="th-status">Status</th>
                    <th class="th-created">Created</th>
                    <th class="th-actions">
                      <button v-if="!selectionMode" class="selection-toggle" @click="toggleSelectionMode">
                        <i class="icon-select"></i>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="course in filteredCourses" 
                    :key="course.id"
                    :class="{ 'selected': selectedCourses.includes(course.id) }"
                  >
                    <td v-if="selectionMode" class="td-checkbox">
                      <input 
                        type="checkbox" 
                        :id="`course-list-${course.id}`" 
                        :checked="selectedCourses.includes(course.id)"
                        @change="toggleCourseSelection(course.id)"
                      >
                      <label :for="`course-list-${course.id}`"></label>
                    </td>
                    <td class="td-title" @click="viewCourse(course.id)">
                      <div class="course-title-cell">
                        <div class="course-thumbnail small">
                          <img v-if="course.thumbnail_url" :src="course.thumbnail_url" :alt="course.title">
                          <div v-else class="thumbnail-placeholder">
                            <i class="icon-course"></i>
                          </div>
                        </div>
                        <span>{{ course.title }}</span>
                      </div>
                    </td>
                    <td class="td-instructor">{{ getInstructorName(course.instructor_id) }}</td>
                    <td class="td-category">{{ getCategoryName(course.category_id) }}</td>
                    <td class="td-lessons">{{ course.lessons_count || 0 }}</td>
                    <td class="td-status">
                      <span class="status-badge" :class="course.status">{{ course.status }}</span>
                    </td>
                    <td class="td-created">{{ formatDate(course.created_at) }}</td>
                    <td class="td-actions">
                      <div class="table-actions">
                        <button class="action-btn view" @click="viewCourse(course.id)" title="View Course">
                          <i class="icon-view"></i>
                        </button>
                        <button class="action-btn edit" @click="editCourse(course.id)" title="Edit Course">
                          <i class="icon-edit"></i>
                        </button>
                        <button class="action-btn delete" @click="confirmDelete(course.id)" title="Delete Course">
                          <i class="icon-delete"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <CoursePagination 
              v-if="totalPages > 1"
              :current-page="currentPage"
              :total-pages="totalPages"
              @change-page="changePage"
            />
          </template>
        </section>
      </div>
    </div>
  </div>
  
  <!-- Dialogs -->
  <teleport to="body">
    <CreateCourseDialog 
      v-if="showCreateDialog" 
      :instructors="instructors" 
      :categories="categories"
      @close="showCreateDialog = false"
      @created="handleCourseCreated"
      @error="handleError"
    />

    <ChangeCategoryDialog
      v-if="showCategoryDialog"
      :categories="categories"
      :selected-count="selectedCourses.length"
      @close="showCategoryDialog = false"
      @change="handleBulkCategoryChange"
    />

    <ChangeStatusDialog
      v-if="showStatusDialog"
      :selected-count="selectedCourses.length"
      @close="showStatusDialog = false"
      @change="handleBulkStatusChange"
    />

    <AIAssistantDialog v-if="showAIDialog" @close="showAIDialog = false" />
  </teleport>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import CreateCourseDialog from '@/components/CreateCourseDialog.vue';
import FilterControls from '@/components/FilterControls.vue';
import MobileHeader from '@/components/MobileHeader.vue';
import CoursePagination from '@/components/Pagination.vue';
import AIAssistantDialog from '@/components/AIAssistantDialog.vue';
import SortingControls from '@/components/SortingControls.vue';
import BulkActionsBar from '@/components/BulkActionsBar.vue';
import ChangeCategoryDialog from '@/components/ChangeCategoryDialog.vue';
import ChangeStatusDialog from '@/components/ChangeStatusDialog.vue';
import { useCourses } from '@/composables/useCourses';
import { useCoursesFilter } from '@/composables/useCoursesFilter';
import { useCoursesExport } from '@/composables/useCoursesExport';

export default defineComponent({
  name: 'CoursesView',
  components: {
    Sidebar,
    CreateCourseDialog,
    FilterControls,
    MobileHeader,
    CoursePagination,
    AIAssistantDialog,
    SortingControls,
    BulkActionsBar,
    ChangeCategoryDialog,
    ChangeStatusDialog
  },
  setup() {
    // State from composables
    const { 
      courses, loading, error, statistics, instructors, categories,
      fetchCourses, fetchStatistics, fetchInstructors, fetchCategories,
      currentPage, totalPages, total, deleteCourse, updateCoursesInBulk
    } = useCourses();
    
    const {
      searchQuery, filters, filteredCourses, 
      updateFilters, updateSearch, normalizeId,
      sortKey, sortOrder, sortBy, resetFilters: resetAllFilters
    } = useCoursesFilter(courses, instructors);

    const { exportCourses } = useCoursesExport(filters, searchQuery);

    // Local state
    const isSidebarOpen = ref(true);
    const showCreateDialog = ref(false);
    const showAIDialog = ref(false);
    const showCategoryDialog = ref(false);
    const showStatusDialog = ref(false);
    const selectionMode = ref(false);
    const selectedCourses = ref([]);
    const viewMode = ref('grid'); // 'grid' or 'list'

    // Computed properties
    const hasActiveFilters = computed(() => {
      return searchQuery.value || 
        Object.values(filters.value).some(val => 
          val !== null && val !== '' && 
          (Array.isArray(val) ? val.length > 0 : true)
        );
    });

    const isAllSelected = computed(() => {
      return filteredCourses.value.length > 0 && 
        filteredCourses.value.every(course => 
          selectedCourses.value.includes(course.id)
        );
    });
    
    const countPublishedCourses = computed(() => {
      return courses.value.filter(course => course.status === 'published').length;
    });
    
    const totalLessonsCount = computed(() => {
      return courses.value.reduce((total, course) => total + (course.lessons_count || 0), 0);
    });

    // Methods
    const toggleSidebar = () => {
      isSidebarOpen.value = !isSidebarOpen.value;
    };

    const viewCourse = (id) => {
      if (!selectionMode.value) {
        window.location.href = `/courses/${id}`;
      }
    };

    const editCourse = (courseId) => {
      if (!selectionMode.value) {
        window.location.href = `/courses/${courseId}/edit`;
      }
    };

    const changePage = async (page) => {
      if (page >= 1 && page <= totalPages.value) {
        await fetchCourses(page);
      }
    };

    const confirmDelete = (courseId) => {
      if (!selectionMode.value && confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
        deleteCourse(courseId);
        fetchCourses();
      }
    };

    const handleCourseCreated = (course) => {
      fetchCourses();
      if (course?.id) {
        window.location.href = `/courses/${course.id}`;
      }
    };

    const handleError = (message) => {
      console.error(message);
      // You might want to use a notification system here
    };

    const handleSort = ({ key }) => {
      sortBy(key);
    };

    const toggleSelectionMode = () => {
      selectionMode.value = !selectionMode.value;
      if (!selectionMode.value) {
        selectedCourses.value = [];
      }
    };

    const toggleCourseSelection = (courseId) => {
      if (selectedCourses.value.includes(courseId)) {
        selectedCourses.value = selectedCourses.value.filter(id => id !== courseId);
      } else {
        selectedCourses.value.push(courseId);
      }
    };

    const toggleSelectAll = () => {
      if (isAllSelected.value) {
        selectedCourses.value = [];
      } else {
        selectedCourses.value = filteredCourses.value.map(course => course.id);
      }
    };

    const deselectAllCourses = () => {
      selectedCourses.value = [];
    };

    const confirmBulkDelete = () => {
      if (selectedCourses.value.length > 0 && confirm(`Are you sure you want to delete ${selectedCourses.value.length} course(s)? This action cannot be undone.`)) {
        Promise.all(selectedCourses.value.map(id => deleteCourse(id)))
          .then(() => {
            fetchCourses();
            selectedCourses.value = [];
          })
          .catch(error => {
            console.error('Error deleting courses:', error);
          });
      }
    };

    const handleBulkCategoryChange = async (categoryId) => {
      if (selectedCourses.value.length > 0) {
        try {
          await updateCoursesInBulk(selectedCourses.value, { category_id: categoryId });
          fetchCourses();
          selectedCourses.value = [];
          showCategoryDialog.value = false;
        } catch (error) {
          console.error('Error updating courses:', error);
        }
      }
    };

    const handleBulkStatusChange = async (status) => {
      if (selectedCourses.value.length > 0) {
        try {
          await updateCoursesInBulk(selectedCourses.value, { status });
          fetchCourses();
          selectedCourses.value = [];
          showStatusDialog.value = false;
        } catch (error) {
          console.error('Error updating courses:', error);
        }
      }
    };

    const getInstructorName = (instructorId) => {
      if (!instructorId) return 'No instructor';
      
      const instructor = instructors.value.find(i => Number(i.user_id) === Number(instructorId));
      if (instructor && instructor.first_name) {
        return `${instructor.first_name} ${instructor.last_name || ''} / ${instructor.first_name_ar || ''} ${instructor.last_name_ar || ''}`;
      }
      
      // Instructor data isn't being loaded, provide a fallback
      return `Instructor #${instructorId}`;
    };

    const getCategoryName = (categoryId) => {
      if (!categoryId) return 'Uncategorized';
      
      // Find the category directly from the array
      const category = categories.value.find(c => Number(c.id) === Number(categoryId));
      if (!category) return `Category #${categoryId}`;
      
      return `${category.title} / ${category.title_ar}`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }).format(date);
    };

    const truncateText = (text, length) => {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    };

    // Lifecycle hooks
    onMounted(async () => {
      try {
        await Promise.all([
          fetchCourses(),
          fetchStatistics(),
          fetchInstructors(),
          fetchCategories()
        ]);
      } catch (error) {
        console.error('Error initializing courses view:', error);
      }
    });

    return {
      // From composables
      courses, loading, error, statistics, instructors, categories,
      fetchCourses, fetchStatistics, fetchInstructors, fetchCategories,
      currentPage, totalPages, total, deleteCourse, updateCoursesInBulk,
      searchQuery, filters, filteredCourses, updateFilters, updateSearch,
      normalizeId, exportCourses, sortKey, sortOrder, sortBy, resetAllFilters,
      
      // Local state
      isSidebarOpen, showCreateDialog, showAIDialog, showCategoryDialog, showStatusDialog,
      selectionMode, selectedCourses, viewMode, hasActiveFilters, isAllSelected,
      countPublishedCourses, totalLessonsCount,
      
      // Methods
      toggleSidebar, viewCourse, editCourse, changePage, confirmDelete,
      handleCourseCreated, handleError, handleSort, toggleSelectionMode,
      toggleCourseSelection, toggleSelectAll, deselectAllCourses,
      confirmBulkDelete, handleBulkCategoryChange, handleBulkStatusChange,
      getInstructorName, getCategoryName, formatDate, truncateText
    };
  }
});
</script>

<style scoped>
/* Component-specific styles */
.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.btn-tooltip {
  display: none;
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--gray-800);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 10;
}

.btn-icon:hover .btn-tooltip {
  display: block;
}

.create-btn {
  padding: 0.625rem 1.25rem;
  font-weight: 500;
}

/* Icons specific to this component */
.icon-export:before {
  content: "\e901";
  font-family: "kewaar-icons";
}

.icon-ai:before {
  content: "\e902";
  font-family: "kewaar-icons";
}

.icon-empty-courses {
  font-size: 4rem;
  color: var(--gray-300);
  margin-bottom: 1.5rem;
}

/* Mobile-specific overrides */
@media (max-width: 768px) {
  .header-actions {
    width: 100%;
    flex-direction: column;
    gap: 1rem;
  }

  .action-buttons {
    width: 100%;
    justify-content: space-between;
  }
}
</style> 